import { useEffect, useMemo, useState } from "react";
import { APIProvider, Map, Polygon, Polyline, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";
import { GOOGLE_MAPS_API_KEY, isGoogleMapsConfigured, GENEVA_CENTER } from "@/config/maps";

/**
 * NeighbourhoodMap — light neighbourhood map with region outline + closer zoom.
 *
 * Tunable parameters:
 * - MAP_ZOOM: used when no polygon bounds are available
 * - HIGHLIGHT_*: region outline / fill (Byron gold)
 * - BYRON_MAP_STYLE: muted cream roadmap
 */

const MAP_ZOOM = 15;

const HIGHLIGHT = {
  stroke: "#C6A87C",
  fill: "#C6A87C",
  fillOpacity: 0.12,
  strokeWeight: 2.25,
  dashRepeat: "12px",
};

const BYRON_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f4f3ef" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6b66" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f4f3ef" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#d9d6cf" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e8df" }, { visibility: "on" }] },
  { featureType: "poi.park", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#e4e1da" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ebe7df" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d8d3c9" }] },
  { featureType: "road.arterial", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#d9e3e6" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#8a9aa0" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#eceae4" }] },
  { featureType: "landscape.man_made", stylers: [{ visibility: "off" }] },
];

const knownCenters = {
  cologny: { lat: 46.2164, lng: 6.1818 },
  champel: { lat: 46.1925, lng: 6.1565 },
  vandoeuvres: { lat: 46.2215, lng: 6.2035 },
  "collonge-bellerive": { lat: 46.253, lng: 6.204 },
  "eaux-vives": { lat: 46.2015, lng: 6.1645 },
  "grand-saconnex": { lat: 46.232, lng: 6.121 },
  verbier: { lat: 46.0969, lng: 7.2285 },
  geneva: GENEVA_CENTER,
  genève: GENEVA_CENTER,
};

const ringToPath = (ring = []) =>
  ring
    .filter((pair) => Array.isArray(pair) && pair.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));

const geoJsonToPaths = (geojson) => {
  if (!geojson?.type || !geojson.coordinates) return [];

  if (geojson.type === "Polygon") {
    // Outer ring only
    const outer = ringToPath(geojson.coordinates[0]);
    return outer.length ? [outer] : [];
  }

  if (geojson.type === "MultiPolygon") {
    return geojson.coordinates
      .map((poly) => ringToPath(poly[0]))
      .filter((path) => path.length > 2);
  }

  return [];
};

const boundsFromBox = (boundingbox) => {
  // Nominatim: [south, north, west, east]
  if (!boundingbox || boundingbox.length < 4) return null;
  const [south, north, west, east] = boundingbox.map(Number);
  if ([south, north, west, east].some((n) => Number.isNaN(n))) return null;
  return {
    south,
    north,
    west,
    east,
  };
};

const pathFromBounds = (bounds) => {
  if (!bounds) return [];
  return [
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.north, lng: bounds.west },
    { lat: bounds.north, lng: bounds.east },
    { lat: bounds.south, lng: bounds.east },
    { lat: bounds.south, lng: bounds.west },
  ];
};

const searchQueriesFor = (location, area) => {
  const loc = (location || "").trim();
  const ar = (area || "").trim();
  const queries = [];
  if (loc) {
    queries.push(`${loc}, Switzerland`);
    if (ar && !/geneva|genève/i.test(ar)) {
      queries.push(`${loc}, ${ar}, Switzerland`);
    }
    // Verbier sits in Val de Bagnes — try canton hint for Alpine resorts
    if (/verbier/i.test(loc)) {
      queries.push("Val de Bagnes, Valais, Switzerland");
      queries.push("Bagnes, Switzerland");
    }
  }
  if (ar) queries.push(`${ar}, Switzerland`);
  return [...new Set(queries)];
};

const fetchNominatim = async (q) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&polygon_geojson=1&addressdetails=1&q=${encodeURIComponent(
    q
  )}`;
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) return null;
  const results = await response.json();
  return results?.[0] || null;
};

const resolvePlace = async (location, area) => {
  const hintKey = (location || "").toLowerCase().trim();
  const fallbackCenter = knownCenters[hintKey] || GENEVA_CENTER;

  try {
    for (const q of searchQueriesFor(location, area)) {
      const result = await fetchNominatim(q);
      if (!result) continue;

      const center = {
        lat: Number(result.lat),
        lng: Number(result.lon),
      };
      const paths = geoJsonToPaths(result.geojson);
      const bounds = boundsFromBox(result.boundingbox);

      // Prefer real administrative polygons; otherwise approximate with bbox ring
      const highlightPaths =
        paths.length > 0 ? paths : bounds ? [pathFromBounds(bounds)] : [];

      if (center.lat && center.lng) {
        return {
          center,
          paths: highlightPaths,
          bounds,
          labelHint: result.display_name,
        };
      }
    }
  } catch (err) {
    console.warn("Neighbourhood map place lookup failed:", err);
  }

  return { center: fallbackCenter, paths: [], bounds: null, labelHint: null };
};

const FitToRegion = ({ center, paths, bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    if (paths?.length) {
      const latLngBounds = new window.google.maps.LatLngBounds();
      paths.forEach((path) => path.forEach((p) => latLngBounds.extend(p)));
      map.fitBounds(latLngBounds, { top: 48, right: 48, bottom: 56, left: 48 });
      return;
    }

    if (bounds) {
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 56, left: 48 });
      return;
    }

    map.setCenter(center);
    map.setZoom(MAP_ZOOM);
  }, [map, center, paths, bounds]);

  return null;
};

const RegionHighlight = ({ paths }) => {
  const dashSymbol = useMemo(
    () => ({
      path: "M 0,-1 0,1",
      strokeOpacity: 1,
      scale: 3,
      strokeColor: HIGHLIGHT.stroke,
    }),
    []
  );

  if (!paths?.length) return null;

  return (
    <>
      {paths.map((path, index) => (
        <Polygon
          key={`fill-${index}`}
          paths={path}
          strokeOpacity={0}
          strokeWeight={0}
          fillColor={HIGHLIGHT.fill}
          fillOpacity={HIGHLIGHT.fillOpacity}
          clickable={false}
        />
      ))}
      {paths.map((path, index) => (
        <Polyline
          key={`stroke-${index}`}
          path={path}
          strokeOpacity={0}
          strokeWeight={HIGHLIGHT.strokeWeight}
          clickable={false}
          icons={[
            {
              icon: dashSymbol,
              offset: "0",
              repeat: HIGHLIGHT.dashRepeat,
            },
          ]}
        />
      ))}
    </>
  );
};

const MapChrome = ({ label, className, children, openHref }) => (
  <div
    className={`aspect-[16/9] bg-[#EFEDE8] relative overflow-hidden media-frame ${className}`}
    data-testid="neighbourhood-map"
  >
    {children}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1]">
      <div className="relative -translate-y-3">
        <div className="w-3.5 h-3.5 rounded-full bg-[#C6A87C] border-2 border-white shadow-md" />
        <div className="absolute left-1/2 top-full -translate-x-1/2 w-px h-3 bg-[#C6A87C]/80" />
      </div>
    </div>
    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3 pointer-events-none z-10">
      <div className="bg-white/95 px-3 py-1.5 text-xs text-[#666660] flex items-center gap-1.5 shadow-sm">
        <MapPin size={12} className="text-[#C6A87C]" />
        {label}
      </div>
      {openHref && (
        <a
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto bg-white/95 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#1A1A1A] hover:text-[#8B7355] transition-colors shadow-sm"
        >
          Open map
        </a>
      )}
    </div>
  </div>
);

const GoogleNeighbourhoodMap = ({ place, label, className }) => {
  const openHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
  const { center, paths, bounds } = place;

  return (
    <MapChrome label={label} className={className} openHref={openHref}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          key={`${center.lat},${center.lng}`}
          defaultCenter={center}
          defaultZoom={MAP_ZOOM}
          gestureHandling="cooperative"
          disableDefaultUi
          zoomControl
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          clickableIcons={false}
          styles={BYRON_MAP_STYLE}
          className="absolute inset-0 w-full h-full"
          style={{ width: "100%", height: "100%" }}
        >
          <FitToRegion center={center} paths={paths} bounds={bounds} />
          <RegionHighlight paths={paths} />
        </Map>
      </APIProvider>
    </MapChrome>
  );
};

const FallbackNeighbourhoodMap = ({ place, label, className }) => {
  const { center } = place;
  const embedSrc = `https://www.google.com/maps?q=${center.lat},${center.lng}&z=${MAP_ZOOM}&hl=fr&output=embed`;
  const openHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;

  return (
    <MapChrome label={label} className={className} openHref={openHref}>
      <iframe
        title={`Map — ${label}`}
        src={embedSrc}
        className="absolute inset-0 w-full h-full border-0 grayscale-[25%] contrast-[0.92] brightness-[1.04] saturate-[0.75]"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </MapChrome>
  );
};

const NeighbourhoodMap = ({ location, area = "Geneva", className = "" }) => {
  // Prefer locality for the label when area is a misleading default (e.g. Verbier + Geneva)
  const label =
    location && area && !new RegExp(location, "i").test(area)
      ? `${location}, ${area}`
      : location || area || "Geneva, Switzerland";

  const [place, setPlace] = useState(() => ({
    center: knownCenters[(location || "").toLowerCase().trim()] || GENEVA_CENTER,
    paths: [],
    bounds: null,
  }));

  useEffect(() => {
    let cancelled = false;
    resolvePlace(location, area).then((next) => {
      if (!cancelled) setPlace(next);
    });
    return () => {
      cancelled = true;
    };
  }, [location, area]);

  if (!isGoogleMapsConfigured) {
    return <FallbackNeighbourhoodMap place={place} label={label} className={className} />;
  }

  return <GoogleNeighbourhoodMap place={place} label={label} className={className} />;
};

export default NeighbourhoodMap;
